/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useRef, useEffect } from 'react';
import { Section, useCitiesContext } from '@spotahome/ui-library';
import { getPagePath, trans } from '@spotahome/soyuz/client';

import withInViewEvent from '@spotahome/ui-library/src/tenant/components/withInViewEvent';
import { useMediaQuery } from '@spotahome/ui-library/src/tenant/components/utils/breakpoints/breakpoints';

import {
  LANDING_SECTIONS,
  TOP_CITIES,
  SEO_LINK_CITIES_KEYS
} from '@spotahome/ui-library/src/tenant/components/utils/seoConstants';
import CityList from '@spotahome/ui-library/src/tenant/components/CityList';

import {
  CarouselArrows,
  MultipleItemsCarousel
} from '../MultipleItemsCarousel';

import City from './City';

// styles
import './CitiesShowcase.scss';

const NUMBER_OF_ITEM_PER_SWIPE_MOBILE = 1;
const NUMBER_OF_ITEM_PER_SWIPE_PHABLET = 2;
const NUMBER_OF_ITEM_PER_SWIPE_TABLET = 3;
const NUMBER_OF_ITEM_PER_SWIPE_DESKTOP = 4;

const COUNTRY_PAGES = {
  FR: 'external.country.page.france',
  ES: 'external.country.page.spain',
  IT: 'external.country.page.italy',
  DE: 'external.country.page.germany',
  PT: 'external.country.page.portugal',
  GB: 'external.country.page.united-kingdom'
};

const getCountryPageUrl = countryIsoCode =>
  COUNTRY_PAGES[countryIsoCode]
    ? getPagePath(COUNTRY_PAGES[countryIsoCode])
    : null;

const CitiesShowcase = () => {
  const { cities } = useCitiesContext();
  const carouselContainerRef = useRef(null);
  const topCitiesKeys = Object.keys(TOP_CITIES);
  const carouselCities = topCitiesKeys
    .map(key => cities.find(city => city.id === key))
    .filter(Boolean);

  const listCities = cities
    .filter(
      city =>
        !topCitiesKeys.includes(city.id) &&
        SEO_LINK_CITIES_KEYS.includes(city.id)
    )
    .sort((a, b) => a.cityName.localeCompare(b.cityName));
  const [page, setPage] = useState(0);
  const mediaQueries = useMediaQuery();

  const getNumberOfItems = () => {
    if (mediaQueries.isBiggerThanTablet()) {
      return NUMBER_OF_ITEM_PER_SWIPE_DESKTOP;
    } else if (mediaQueries.isBiggerThanPhablet()) {
      return NUMBER_OF_ITEM_PER_SWIPE_TABLET;
    } else if (mediaQueries.isBiggerThanMobile()) {
      return NUMBER_OF_ITEM_PER_SWIPE_PHABLET;
    } else if (
      mediaQueries.isMobileScreen() ||
      mediaQueries.isPhabletScreen()
    ) {
      return NUMBER_OF_ITEM_PER_SWIPE_MOBILE;
    }

    // default
    return NUMBER_OF_ITEM_PER_SWIPE_DESKTOP;
  };

  const getSlidePercentage = () =>
    mediaQueries.isBiggerThanTablet() ? 100 : 90;

  const numberOfItems = getNumberOfItems();

  useEffect(() => {
    setPage(0);
  }, [mediaQueries.device]);

  return (
    <Section background="light" className="cities-showcase__container">
      <Section background="light" className="cities-showcase__top-cities">
        <div className="cities-showcase">
          <div className="cities-showcase__header">
            <h2 className="cities-showcase__header-title">
              {trans('landingpage.label.cities')}
            </h2>
            <div className="cities-showcase__arrows">
              <CarouselArrows
                page={page}
                setPage={setPage}
                maxPage={Math.ceil(carouselCities.length / numberOfItems)}
                isRedesign
              />
            </div>
          </div>
          <div className="cities-showcase__carousel" ref={carouselContainerRef}>
            <MultipleItemsCarousel
              items={carouselCities.map(city => (
                <City
                  key={city.id}
                  id={city.id}
                  countryId={city.country.countryId.toLowerCase()}
                  countryPageUrl={getCountryPageUrl(
                    city.country.countryIsoCode
                  )}
                />
              ))}
              itemsPerSwipe={numberOfItems}
              selectedPage={page}
              slidePercentage={getSlidePercentage()}
            />
          </div>
        </div>
      </Section>
      <Section background="light" className="cities-showcase__other-cities">
        <CityList cities={listCities} />
      </Section>
    </Section>
  );
};

export default withInViewEvent(LANDING_SECTIONS.citiesShowcase)(CitiesShowcase);
